var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useCallback, useRef, useEffect, useContext, useMemo, memo } from 'react';
import BaseTableComponent, { processColumns } from './Common/BaseTableComponent';
import PaginationComponent from '../Pagination/PaginationComponent';
import useToast from '@notifications/Toast/useToast';
import { LoadingSpinner } from '@widget/loaders';
import { UXPContext } from '@core/context/Context';
import { hasValue, textSearch } from '@utils';
import SearchBox from '@widget/forms/inputs/SearchBox';
import useDebounce from '@hooks/useDebounce';
import { FilterView, cleanFilters } from './object-search/FilterView';
const TableComponentBase = (props) => {
    var _a, _b;
    const context = useContext(UXPContext);
    const toast = useToast();
    const tableContainerRef = useRef(null);
    const timerRef = useRef(null);
    const minCellWidth = (props === null || props === void 0 ? void 0 : props.minCellWidth) || 100;
    const [containerWidth, setContainerWidth] = useState(0);
    const [columns, setColumns] = useState(null);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(props.pageSize || 10);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState('');
    const [filters, setFilters] = useState(null);
    const [showFilterPanel, setShowFilterPanel] = useState(false);
    const debouncedQuery = useDebounce(query, 300);
    const debouncedFilters = useDebounce(filters, 300);
    const finalPageSize = props.disablePagination ? 1000 : pageSize;
    const applyLocalSearch = useCallback((items, q) => {
        var _a, _b;
        if (!q || !((_b = (_a = props.search) === null || _a === void 0 ? void 0 : _a.fields) === null || _b === void 0 ? void 0 : _b.length))
            return items;
        return items.filter(item => props.search.fields.some(f => textSearch(item[f], q, true)));
    }, [props.search]);
    const loadData = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        try {
            setLoading(true);
            if (typeof props.data === 'function') {
                const res = yield props.data(page, finalPageSize, debouncedQuery, debouncedFilters);
                setData((res === null || res === void 0 ? void 0 : res.items) || []);
            }
            else {
                const filtered = applyLocalSearch(props.data || [], debouncedQuery);
                const start = Math.max((page - 1) * finalPageSize, 0);
                setData(filtered.slice(start, start + finalPageSize));
            }
        }
        catch (e) {
            toast.error('Unable to get data. something went wrong');
            console.error('Unable to get data:', e);
        }
        finally {
            setLoading(false);
        }
    }), [page, finalPageSize, props.data, props.loading, debouncedQuery, debouncedFilters, applyLocalSearch]);
    const handleResize = useCallback((id, newWidth) => {
        setColumns(prev => {
            if (!(prev === null || prev === void 0 ? void 0 : prev[id]))
                return prev;
            const column = prev[id];
            const width = hasValue(column.maxWidth)
                ? Math.min(column.maxWidth, Math.max(newWidth, column.minWidth))
                : Math.max(newWidth, column.minWidth);
            return Object.assign(Object.assign({}, prev), { [id]: Object.assign(Object.assign({}, column), { width }) });
        });
    }, []);
    const updateContainerWidth = useCallback(() => {
        if (tableContainerRef.current) {
            const width = tableContainerRef.current.getBoundingClientRect().width;
            setContainerWidth(width);
        }
    }, []);
    useEffect(() => {
        updateContainerWidth();
        const resizeObserver = new ResizeObserver(updateContainerWidth);
        if (tableContainerRef.current)
            resizeObserver.observe(tableContainerRef.current);
        return () => resizeObserver.disconnect();
    }, []);
    useEffect(() => {
        var _a, _b, _c;
        if (containerWidth > 0) {
            const defaultActionColums = {};
            if ((_a = props === null || props === void 0 ? void 0 : props.editColumn) === null || _a === void 0 ? void 0 : _a.enable)
                defaultActionColums.editColumn = Object.assign(Object.assign({}, props.editColumn), { width: 60 });
            if ((_b = props === null || props === void 0 ? void 0 : props.deleteColumn) === null || _b === void 0 ? void 0 : _b.enable)
                defaultActionColums.deleteColumn = Object.assign(Object.assign({}, props.deleteColumn), { width: 60 });
            if (!!((_c = props === null || props === void 0 ? void 0 : props.editable) === null || _c === void 0 ? void 0 : _c.onReorderItems))
                defaultActionColums.dragHandleColumn = { enable: true, width: 40 };
            const newColumns = processColumns({
                context,
                columns: props.columns,
                defaultColumns: defaultActionColums,
                containerWidth,
                minCellWidth,
                editableMode: !!(props.editable && (props.editable.onAddItem || props.editable.onUpdateItem || props.editable.onDeleteItem))
            });
            if (newColumns)
                setColumns(newColumns);
        }
    }, [containerWidth, props.columns, props.editColumn, props.deleteColumn, props.editable, minCellWidth, context]);
    useEffect(() => {
        if (typeof props.total === 'function') {
            props.total(debouncedQuery, debouncedFilters).then(setTotal).catch(console.error);
        }
        else if (Array.isArray(props.data)) {
            const filtered = applyLocalSearch(props.data || [], debouncedQuery);
            setTotal(filtered.length);
        }
        else if (typeof props.total === 'number') {
            setTotal(props.total);
        }
    }, [props.total, props.data, debouncedQuery, debouncedFilters, applyLocalSearch]);
    // Reset page to 1 on query/filter change
    useEffect(() => {
        setPage(1);
    }, [debouncedQuery, debouncedFilters]);
    useEffect(() => {
        if (timerRef.current)
            clearTimeout(timerRef.current);
        timerRef.current = setTimeout(loadData, 300);
        return () => { if (timerRef.current)
            clearTimeout(timerRef.current); };
    }, [loadData]);
    const totalWidth = useMemo(() => {
        if (!columns)
            return '100%';
        return Object.values(columns).reduce((total, { width }) => total + width, 0);
    }, [columns]);
    const hasFiltersConfig = !!props.filters && (!!((_a = props.filters.formFields) === null || _a === void 0 ? void 0 : _a.length) || !!props.filters.renderFilters);
    const hasSearch = !!((_b = props.search) === null || _b === void 0 ? void 0 : _b.enable);
    const hasToolbar = hasFiltersConfig || hasSearch || !!props.actionButtons || !props.disablePagination;
    return (_jsxs("div", { className: "uxpcore_tablecomponent__wrapper", children: [hasToolbar && (_jsxs("div", { className: "uxpcore_tablecomponent__toolbar", children: [_jsxs("div", { className: "uxpcore_tablecomponent__toolbar-left", children: [hasFiltersConfig && (_jsx(FilterView, { isOpen: showFilterPanel, onToggle: () => setShowFilterPanel(p => !p), appliedFilters: filters, onChange: f => setFilters(cleanFilters(f)), formFields: props.filters.formFields || [], renderCustom: props.filters.renderFilters })), hasSearch && (_jsx("div", { className: "uxpcore_tablecomponent__search", children: _jsx(SearchBox, { value: query, onChange: setQuery }) }))] }), _jsxs("div", { className: "uxpcore_tablecomponent__toolbar-right", children: [props.actionButtons, !props.disablePagination && (_jsx(PaginationComponent, { total: total, pageSize: pageSize, page: page, dataLength: data.length, onPageChange: setPage, onPageSizeChange: ps => { setPageSize(ps); setPage(1); }, loading: props.loading || loading }))] })] })), _jsx("div", { className: "uxpcore_tablecomponent__container", ref: tableContainerRef, children: (!columns)
                    ? _jsx(LoadingSpinner, {})
                    : (_jsxs(_Fragment, { children: [_jsx("div", { className: "uxpcore_tablecomponent__body", children: _jsx(BaseTableComponent, { columns: columns, data: data, loading: props.loading || loading, noItemsMessage: props.noItemsMessage, totalWidth: totalWidth, onClickRow: props.onClickRow, handleResize: handleResize, editable: props.editable }) }), props.addNewRow && (_jsx("div", { className: "uxpcore_tablecomponent__addnew", children: props.addNewRow }))] })) })] }));
};
/**
 * @export
 * TableComponent displays tabular data with optional pagination, search, filters,
 * action buttons, edit/delete actions, and inline editing mode.
 *
 * @example
 * ```tsx
 * // Basic table with static data
 * <TableComponent
 *   data={[{ id: 1, name: 'Item 1' }]}
 *   columns={[{ id: 'name', label: 'Name' }]}
 *   pageSize={10}
 *   total={1}
 * />
 * ```
 *
 * @example
 * ```tsx
 * // With search, filters and action buttons
 * <TableComponent
 *   data={items}
 *   columns={columns}
 *   pageSize={10}
 *   total={items.length}
 *   search={{ enable: true, fields: ['name'] }}
 *   filters={{ formFields: [{ title: '', columns: 1, fields: [{ name: 'status', label: 'Status', type: 'text' }] }] }}
 *   actionButtons={<Button title="Add" onClick={handleAdd} />}
 * />
 * ```
 */
const TableComponent = memo((props) => {
    return _jsx(TableComponentBase, Object.assign({}, props));
});
export default TableComponent;
